import type { BridgeChartData, BridgeChartBar } from "../types/data";
import type { BridgeChartViewport } from "../types/public";

const invalid = Symbol("invalid supplied scalar");
const lineageKeys = ["priceBasis", "historySourceSnapshotId", "historySourceHash", "historyAcquiredAt"] as const;
const scalar = (value: unknown) => value === null || value === undefined ? value
  : typeof value === "string" && value.length <= 128 ? value : invalid;
const text = (value: unknown, maximum: number): value is string => typeof value === "string" && value.length > 0 && value.length <= maximum;

export interface SelectedBarAssignment {
  readonly bars: BridgeChartData["bars"];
  readonly symbol: string;
  readonly timeframe: string;
  readonly metadataSymbol: string;
  readonly metadataTimeframe: string;
  readonly lineage: readonly unknown[];
}

export interface SelectedPriceSource {
  basis: string | null;
  snapshot: string | null;
  hash: string | null;
  acquired: string | null;
}

export type SelectedBarContext = { state: "ContextUnavailable" }
  | { state: "AwaitingSelection"; source: SelectedPriceSource }
  | { state: "Selected"; source: SelectedPriceSource; symbol: string; timeframe: string;
      bar: BridgeChartBar; visibleFirst: string; visibleLast: string };

/** Capture scalar values rather than a mutable metadata-object reference. No archive index/clone. */
export function captureSelectedBarAssignment(data: BridgeChartData): SelectedBarAssignment {
  return { bars: data.bars, symbol: data.symbol, timeframe: data.timeframe,
    metadataSymbol: data.metadata.symbol, metadataTimeframe: data.metadata.timeframe,
    lineage: lineageKeys.map(key => scalar(data.metadata[key])) };
}

function isoDate(value: unknown): value is string {
  if (!text(value, 20) || !/^\d{4}-\d{2}-\d{2}(?:T\d{2}:\d{2}:\d{2}Z)?$/.test(value)) return false;
  const at = Date.parse(value.length === 10 ? `${value}T00:00:00Z` : value);
  return Number.isFinite(at) && (value.length === 10 ? new Date(at).toISOString().slice(0, 10)
    : new Date(at).toISOString().slice(0, 19) + "Z") === value;
}

function acquisition(value: unknown): value is string {
  if (!text(value, 40) || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,7})?(?:Z|[+-]\d{2}:\d{2})$/.test(value) || !isoDate(value.slice(0, 10))) return false;
  const hour = Number(value.slice(11, 13)), minute = Number(value.slice(14, 16)), second = Number(value.slice(17, 19));
  const offset = /[+-](\d{2}):(\d{2})$/.exec(value);
  return hour < 24 && minute < 60 && second < 60 && (!offset || Number(offset[1]) <= 14
    && Number(offset[2]) < 60 && (Number(offset[1]) < 14 || Number(offset[2]) === 0)) && Number.isFinite(Date.parse(value));
}

/** One selected read and at most two viewport-edge reads; never search a nearby date. */
export function projectSelectedBarContext(assignment: SelectedBarAssignment, data: BridgeChartData,
  viewport: BridgeChartViewport, event: unknown): SelectedBarContext {
  if (!text(data.symbol, 128) || !text(data.metadata.symbol, 128) || data.bars !== assignment.bars || data.symbol !== assignment.symbol || data.timeframe !== assignment.timeframe
      || data.metadata.symbol !== assignment.metadataSymbol || data.metadata.timeframe !== assignment.metadataTimeframe
      || data.symbol !== data.metadata.symbol || data.timeframe !== "1D" || data.metadata.timeframe !== "1D"
      || lineageKeys.some((key, i) => scalar(data.metadata[key]) !== assignment.lineage[i])) return { state: "ContextUnavailable" };
  const metadata = data.metadata;
  const source: SelectedPriceSource = {
    basis: text(metadata.priceBasis, 80) ? metadata.priceBasis : null,
    snapshot: text(metadata.historySourceSnapshotId, 36) && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(metadata.historySourceSnapshotId)
      && !/^0{8}-0{4}-0{4}-0{4}-0{12}$/.test(metadata.historySourceSnapshotId) ? metadata.historySourceSnapshotId : null,
    hash: text(metadata.historySourceHash, 64) && /^[0-9a-f]{64}$/i.test(metadata.historySourceHash) ? metadata.historySourceHash : null,
    acquired: acquisition(metadata.historyAcquiredAt) ? metadata.historyAcquiredAt : null
  };
  const waiting: SelectedBarContext = { state: "AwaitingSelection", source };
  if (!event || typeof event !== "object" || !("state" in event) || event.state !== "Inspected"
      || !("index" in event) || typeof event.index !== "number" || !Number.isInteger(event.index)
      || !("date" in event) || typeof event.date !== "string" || event.date.length > 20
      || !Number.isFinite(viewport.start) || !Number.isFinite(viewport.end) || viewport.start > viewport.end
      || event.index < viewport.start || event.index > viewport.end || event.index < 0 || event.index >= data.bars.length) return waiting;
  const bar = data.bars[event.index];
  if (!bar || bar.date !== event.date || !isoDate(bar.date)
      || ![bar.open, bar.high, bar.low, bar.close, bar.volume].every(value => typeof value === "number" && Number.isFinite(value))
      || bar.volume < 0 || bar.high < bar.low || bar.high < Math.max(bar.open, bar.close) || bar.low > Math.min(bar.open, bar.close)) return waiting;
  const first = data.bars[Math.max(0, Math.ceil(viewport.start))], last = data.bars[Math.min(data.bars.length - 1, Math.floor(viewport.end))];
  if (!first || !last || !isoDate(first.date) || !isoDate(last.date)) return waiting;
  return { state: "Selected", source, symbol: data.symbol, timeframe: data.timeframe,
    bar: { date: bar.date, open: bar.open, high: bar.high, low: bar.low, close: bar.close, volume: bar.volume },
    visibleFirst: first.date, visibleLast: last.date };
}
